import { Dialog } from '@headlessui/react';
import { MathFieldEditor, useRichTextEditor } from '@shared/rich-text-editor-core';
import katex from 'katex';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '../../base';
import { LatexIcon } from '../../icons';

import ControlButton from './control-button';

export interface LatexInsertModalProps {
  open: boolean;
  onCancel: () => void;
  onInsert: (latex: string) => void;
}

const LATEX_ERROR_MESSAGE = 'กรุณากรอกสัญลักษณ์ทางคณิตศาสตร์ให้ถูกต้อง';

export function LatexInsertModal(props: LatexInsertModalProps) {
  const { open, onCancel, onInsert } = props;
  const { watch, reset, getValues, setValue } = useForm<{ latex: string }>({
    defaultValues: { latex: '' },
  });
  const [error, setError] = useState<string | null>(null);
  const mathFieldRef = useRef<HTMLDivElement>(null);

  const value = watch('latex');

  const handleChange = (newValue: string) => {
    setValue('latex', newValue, { shouldValidate: true });
  };

  const handleClose = () => {
    reset({ latex: '' });
    onCancel();
    setError(null);
  };

  useEffect(() => {
    if (open) {
      reset({ latex: '' });
      setError(null);
    }
  }, [open, reset]);

  useEffect(() => {
    if (typeof value === 'string') {
      try {
        katex.renderToString(value, { throwOnError: true });
        setError(null);
      } catch {
        setError(LATEX_ERROR_MESSAGE);
      }
    }
  }, [value]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={() => undefined}
      className='relative z-[9999]'
      static
      unmount={false}
    >
      <div
        className='fixed inset-0 bg-black opacity-60'
        onClick={handleClose}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            handleClose();
          }
        }}
        role='button'
        tabIndex={0}
      />
      <div className='fixed inset-0 overflow-y-auto'>
        <div className='flex min-h-full items-center justify-center p-4'>
          <Dialog.Panel className='relative w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white p-0 shadow-md transition-all'>
            <div className='px-8 py-6'>
              <Dialog.Title as='h3' className='text-2xl font-semibold text-neutral-900'>
                Insert LaTeX
              </Dialog.Title>
            </div>

            <div ref={mathFieldRef} className='m-8 flex max-w-full flex-col items-start gap-4'>
              <div className='w-full rounded-xs border'>
                <MathFieldEditor onChange={handleChange} initialValue={value} />
              </div>
              {error ? <p className='text-sm text-red-600'>{error}</p> : null}
            </div>

            <div className='flex justify-end gap-2 px-8 pb-8'>
              <Button variant='text' onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  try {
                    const latex = String(getValues('latex') ?? '');
                    katex.renderToString(latex, { throwOnError: true });
                    setError(null);
                    onInsert(latex);
                    handleClose();
                  } catch {
                    setError(LATEX_ERROR_MESSAGE);
                  }
                }}
              >
                Insert
              </Button>
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}

function LatexControl() {
  const { editor } = useRichTextEditor();
  const [open, setOpen] = useState(false);

  if (!editor) return null;

  return (
    <>
      <ControlButton onClick={() => setOpen(true)}>
        <LatexIcon />
      </ControlButton>
      <LatexInsertModal
        open={open}
        onCancel={() => setOpen(false)}
        onInsert={(latex) => {
          editor.chain().focus().insertLatex(latex).run();
          setOpen(false);
        }}
      />
    </>
  );
}

export default LatexControl;
export { LatexControl as RichTextEditorLatexControl };
